import {
  WebGLRenderer,
  Scene,
  PerspectiveCamera,
  Color,
  SRGBColorSpace,
  Vector3,
  Quaternion,
  Matrix4,
  Group,
  Mesh,
  SphereGeometry,
  CircleGeometry,
  ConeGeometry,
  CylinderGeometry,
  MeshBasicMaterial,
  BufferGeometry,
  Float32BufferAttribute,
  Line,
  LineDashedMaterial,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { useEffect, useState, useRef, useCallback } from "react";
import { addDirectionalLight, addGrid, addAxes } from "../helpers/SceneHelpers";
import { loadModel } from "../helpers/ModelLoader";
import { useCamera } from '../../context/CameraContext'; // useCameraをインポート

// 視点を表す「人の目」オブジェクトを生成する。
// lookAt(target) で配置すると、目の前面（虹彩・瞳孔）が target 側を向く（ローカル -z が target を向くため）。
// 同時に、枠外にいることを示す外向きの矢印（ローカル +z＝原点と反対側）を子に持つ。
// 眼球はオレンジ：背景(0xf5f5f5)や白い立方体モデルの上でも視点マーカーとして識別できる色。
// MeshBasicMaterial なので光源の影響を受けず、どの視点からでも同じ色に見える。
function createEyeObject(radius = 60) {
  const group = new Group();
  const eyeball = new Mesh(
    new SphereGeometry(radius, 24, 16),
    new MeshBasicMaterial({ color: 0xff8c00, transparent: true, opacity: 1 })
  );
  group.add(eyeball);
  // 虹彩（青）と瞳孔（黒）を target 側（ローカル -z）の表面に貼る
  const iris = new Mesh(
    new CircleGeometry(radius * 0.55, 24),
    new MeshBasicMaterial({ color: 0x2a6fdb, transparent: true, opacity: 1 })
  );
  iris.rotation.y = Math.PI; // CircleGeometry の法線(+z)を -z に向ける
  iris.position.set(0, 0, -radius * 1.01);
  const pupil = new Mesh(
    new CircleGeometry(radius * 0.28, 24),
    new MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 1 })
  );
  pupil.rotation.y = Math.PI;
  pupil.position.set(0, 0, -radius * 1.02);
  group.add(iris, pupil);

  // 枠外マーカー（外向き矢印）。原点と反対方向（ローカル +z）を指す。
  const arrow = new Mesh(
    new ConeGeometry(radius * 0.5, radius * 1.1, 20),
    new MeshBasicMaterial({ color: 0xe8453c })
  );
  arrow.rotation.x = Math.PI / 2; // 円錐の軸(+y)を +z に向ける（外向き）
  arrow.position.set(0, 0, radius * 1.4);
  arrow.visible = false;
  group.add(arrow);

  group.userData.isEyeObject = true;
  group.userData.beyondArrow = arrow;
  group.userData.fadeMeshes = [eyeball, iris, pupil];
  return group;
}

// 視点が動いた「向き」を示す矢印（2026-10-19）。長さは一定で、動いた距離は表さない
// （cube_model3 の一致視点は既定から約50しか動かず、目の半径60より短いので、道のりの矢印では目に隠れるため）。
// ローカル +y が矢印の向き。目の子にすると lookAt で回ってしまうので、シーン直下に置き毎フレーム位置と向きを合わせる。
// 色は目と同じオレンジ系（視点マーカー専用色）で、目より濃くして区別する。
const EYE_ARROW_OFFSET = 60 * 1.15; // 目の中心から矢印の付け根まで（目の半径の少し外）
const EYE_ARROW_LENGTH = 70 + 44; // 矢印の長さ（軸＋先端）
function createDirectionArrow() {
  const group = new Group();
  const mat = new MeshBasicMaterial({ color: 0xc2410c });
  const shaft = new Mesh(new CylinderGeometry(9, 9, 70, 16), mat);
  shaft.position.y = 35;
  const head = new Mesh(new ConeGeometry(26, 44, 20), mat);
  head.position.y = 70 + 22;
  group.add(shaft, head);
  return group;
}

// 原点を見るカメラの向き（上は世界の上）。演習のカメラはこの向きで動く（lookAt）
const WORLD_UP = new Vector3(0, 1, 0);
const ORIGIN = new Vector3(0, 0, 0);
function cameraTurnAt(p) {
  return new Quaternion().setFromRotationMatrix(new Matrix4().lookAt(p, ORIGIN, WORLD_UP));
}

// 目が from→to へ動くとき、画面を目と一緒に動かして見せるための値（2026-10-19）。
// 「おかしさが分かる視点」では、カメラが目と一緒に動くので目は画面上で止まって見え、
// 3Dモデルのほうが逆向きに回って見える（学習者の視点が動くことを、目を基準にした見え方で示す）。
// worldTurn＝世界側（モデル・光源）を回す回転＝「誤り」列のカメラの向きの変化（from→to）の逆。
//   2つの向きを最短でつなぐ回転（setFromUnitVectors）だと、左右に回り込む動き（cube_model3）で回転軸が
//   鉛直から傾き、真上からの画面で世界が横倒しに見えてしまうため、カメラの向き（上は世界の上）どうしで求める。
// tangent＝from での動き出しの向き（目と一緒に動く画面の中では一定なので、矢印の向きに使う）。
function followMotion(from, to) {
  if (!from || !to) return null;
  const a = new Vector3(from.x, from.y, from.z);
  const b = new Vector3(to.x, to.y, to.z);
  if (a.lengthSq() < 1e-6 || b.lengthSq() < 1e-6) return null;
  const radial = a.clone().normalize();
  const tangent = b.clone().sub(a);
  tangent.addScaledVector(radial, -tangent.dot(radial));
  if (tangent.lengthSq() < 1e-6) return null;
  const worldTurn = cameraTurnAt(a).multiply(cameraTurnAt(b).invert());
  return { worldTurn, tangent: tangent.normalize() };
}

// 原点と視点を結ぶ点線（破線）。LineDashedMaterial を使うため computeLineDistances が必要。
function createGazeLine() {
  const geom = new BufferGeometry();
  geom.setAttribute('position', new Float32BufferAttribute([0, 0, 0, 0, 0, 0], 3));
  const mat = new LineDashedMaterial({ color: 0x444444, dashSize: 18, gapSize: 12, linewidth: 1 });
  const line = new Line(geom, mat);
  line.computeLineDistances();
  line.userData.isGazeLine = true;
  return line;
}

// worldPos をカメラのNDCに投影し、枠外なら「原点→worldPos」の線分が枠(±margin)と交わる点へクランプする。
// 戻り値: { offFrame, world } world はクランプ後のワールド座標。
// margin は 1 未満にすること：1.05 だと目の中心が画面外に置かれ、眼球の縁しか見えない
// （cube_model5 等の真横ヒント視点で「目がほぼ見えない・動いて見えない」原因だった）。
function clampGazeToFrame(camera, worldPos, margin = 0.85) {
  const o = new Vector3(0, 0, 0).project(camera); // 原点のNDC（カメラは原点を向くのでほぼ中央）
  const e = new Vector3(worldPos.x, worldPos.y, worldPos.z).project(camera); // 真の視点のNDC
  const behind = e.z > 1; // カメラ背後
  const offFrame = behind || Math.abs(e.x) > margin || Math.abs(e.y) > margin;
  if (!offFrame) {
    return { offFrame: false, world: new Vector3(worldPos.x, worldPos.y, worldPos.z) };
  }
  // 原点(枠内)から e へ進み、最初に枠(±margin)を出る点を slab 法で求める
  const dx = e.x - o.x;
  const dy = e.y - o.y;
  let tExit = 1;
  if (dx > 1e-6) tExit = Math.min(tExit, (margin - o.x) / dx);
  else if (dx < -1e-6) tExit = Math.min(tExit, (-margin - o.x) / dx);
  if (dy > 1e-6) tExit = Math.min(tExit, (margin - o.y) / dy);
  else if (dy < -1e-6) tExit = Math.min(tExit, (-margin - o.y) / dy);
  tExit = Math.max(0, Math.min(1, tExit));
  const ndcX = o.x + dx * tExit;
  const ndcY = o.y + dy * tExit;
  // 奥行きは真の視点と同じにして見かけの大きさを保つ（背後の場合は手前に寄せる）
  const ndcZ = behind ? 0.5 : e.z;
  const world = new Vector3(ndcX, ndcY, ndcZ).unproject(camera);
  return { offFrame: true, world };
}

function disposeObject3D(obj) {
  obj.traverse((node) => {
    if (node.geometry) {
      try { node.geometry.dispose(); } catch (_) {}
    }
    if (node.material) {
      const mats = Array.isArray(node.material) ? node.material : [node.material];
      mats.forEach((m) => { try { m.dispose(); } catch (_) {} });
    }
  });
}

const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

export function useThreeRenderer(
  glbPath,
  canvasRef,
  isModelVisible,
  isGridVisible,
  isOrbitControlsEnabled,
  shearValue, // シークバーの値
  addColor, // 追加: 部位着色指定文字列 (例: "Plate-rimGeometry")
  cameraPosition, // カメラ位置
  cameraTarget, // カメラターゲット位置
  cameraTiltAngle = 0, // カメラの首傾げ角度（ラジアン）
  persistentEyePosition = null, // 指定時：シーン構築時に目オブジェクトをこの位置へ静的配置（再構築後も残る）
  persistentEyeFrom = null // 指定時：目が persistentEyeFrom から persistentEyePosition へ動いた後を、画面を目と一緒に動かした見え方で示す
  // （目は persistentEyeFrom の位置に見え、世界側がその動きの逆向きに回る。目には動いた向きの矢印を添える）
) {
  const { setCameraPosition } = useCamera(); // setCameraPositionを取得
  const [renderer, setRenderer] = useState(null);
  const [scene, setScene] = useState(null);
  const [isRendererReady, setIsRendererReady] = useState(false);
  const cameraRef = useRef(null); // カメラを保持
  const controlsRef = useRef(null); // OrbitControlsを保持
  const modelRef = useRef(null); // 現在のモデルを保持
  const sceneRef = useRef(null); // 命令的アクセス用にシーンを保持
  const eyeRef = useRef(null); // 視点を表す目オブジェクト
  const eyeLineRef = useRef(null); // 原点と視点を結ぶ点線
  const eyeTruePosRef = useRef(null); // 目の論理位置（クランプ前のワールド座標）
  const frameRef = useRef(null); // 世界側（モデル・光源）をまとめたグループ。目と一緒に動く画面ではこれを回す
  const frameAnimRef = useRef(null); // 世界側を回すアニメーション { to: Quaternion, duration, start }
  const eyeArrowRef = useRef(null); // 視点が動いた向きの矢印
  const eyeDirRef = useRef(null); // 矢印の向き（単位ベクトル）
  const gazeRafRef = useRef(null); // 命令的カメラアニメーションのrAF
  const builtColorRef = useRef(addColor); // いまのシーンに載っているモデルの着色指定
  const loadTokenRef = useRef(0); // 最後に始めたモデル読み込みの番号（古い読み込みの結果は捨てる）
  // 読み込んだモデルをシーンに載せる（前のモデルは外す）。読み込みは非同期なので、
  // 後から始めた読み込みやシーンの作り直しに追い越された結果は捨てる
  const placeModel = (sc, promise) => {
    const token = ++loadTokenRef.current;
    Promise.resolve(promise).then((model) => {
      if (!model || token !== loadTokenRef.current || sceneRef.current !== sc) return;
      const parent = sc.userData.frame || sc; // モデルは世界側のグループに載せる（目と一緒に動く画面で一緒に回すため）
      if (modelRef.current) parent.remove(modelRef.current);
      parent.add(model);
      modelRef.current = model; // モデルを保持
      setIsRendererReady(true); // レンダラーが準備完了
    });
  };
  // console.log("useThreeRenderer called with:", { glbPath, isModelVisible, isGridVisible, isOrbitControlsEnabled, shearValue, addColor, cameraPosition });

  useEffect(() => {
    if (!isModelVisible) return;

    const canvas = canvasRef?.current;
    if (!canvas) {
      console.error("Canvas element ref not found");
      return;
    }

    // 再構築（glbPath 変更等）の間は「準備完了」を解除する。
    // これがないと ready=true のまま新モデルのロードが走り、
    // 利用側が旧シーンをキャプチャする・切替を検知できない、が起こる。
    setIsRendererReady(false);
    // 前のシーンのモデルを指したままにしない（着色の差し替えが前のモデルを消そうとしないように）
    modelRef.current = null;
    builtColorRef.current = addColor;

    // Renderer setup - 最初にネイティブの WebGL コンテキストを取得して検証する
    const gl = canvas.getContext && (
      canvas.getContext('webgl2', { antialias: true }) ||
      canvas.getContext('webgl', { antialias: true }) ||
      canvas.getContext('experimental-webgl', { antialias: true })
    );
    if (!gl) {
      console.error('useThreeRenderer: Failed to obtain WebGL context for canvas. Skipping renderer creation.');
      return;
    }

    const rendererInstance = new WebGLRenderer({ canvas, context: gl, antialias: true });
    // canvas のクライアントサイズが 0 の場合もあるためフォールバックを許容する
    const width = canvas.clientWidth || canvas.width || 800;
    const height = canvas.clientHeight || canvas.height || 600;
    rendererInstance.setSize(width, height);
    if (rendererInstance.outputColorSpace !== undefined) rendererInstance.outputColorSpace = SRGBColorSpace;

    // Scene setup
    const sceneInstance = new Scene();
    sceneInstance.background = new Color(0xf5f5f5);
    sceneRef.current = sceneInstance;
    // シーンを作り直すと前の目オブジェクト・点線は破棄されるため参照をクリア
    eyeRef.current = null;
    eyeLineRef.current = null;
    eyeTruePosRef.current = null;
    frameAnimRef.current = null;
    eyeArrowRef.current = null;
    eyeDirRef.current = null;
    // 世界側のグループ（原点まわりに回す）。目・点線・矢印・グリッドは画面側の表示なので入れない
    const frame = new Group();
    sceneInstance.add(frame);
    sceneInstance.userData.frame = frame;
    frameRef.current = frame;

    // Camera setup
    const aspect = canvas.clientWidth / canvas.clientHeight;
    const camera = new PerspectiveCamera(45, aspect, 1, 10000);
    if (cameraPosition) {
      camera.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z);
    } else {
      camera.position.set(0, 194, -461);
    }
    cameraRef.current = camera;

    const controls = new OrbitControls(camera, rendererInstance.domElement);
    controls.enabled = isOrbitControlsEnabled; // ここで有効/無効を設定
    if (cameraTarget) {
      controls.target.set(cameraTarget.x, cameraTarget.y, cameraTarget.z);
      camera.lookAt(cameraTarget.x, cameraTarget.y, cameraTarget.z);
    } else {
      controls.target.set(0, 0, 0);
      camera.lookAt(0, 0, 0);
    }
    controlsRef.current = controls;

    // カメラ位置が変更されたときにCameraContextを更新
    const onControlsChange = () => {
      setCameraPosition({
        x: camera.position.x,
        y: camera.position.y,
        z: camera.position.z,
      });
    };
    controls.addEventListener('change', onControlsChange);

    // Lighting setup（光源は世界側。目と一緒に動く画面ではモデルと一緒に回り、陰影が変わらない）
    addDirectionalLight(frame);

    // Grid and axes helpers（画面側に置き、目と一緒に動く画面でも回さない。
    // 回すとグリッドの面を斜めから見ることになり、濃い帯になって3Dモデルにかかるため）
    if (isGridVisible) {
      addGrid(sceneInstance);
      addAxes(sceneInstance);
    }

    // 「おかしさが解消された視点」の手掛かりとして目オブジェクトを静的配置する。
    // playEyeAnimation（命令的）と違いシーン構築の一部なので、モデル差し替えによる
    // 再構築後も目が残り、カメラと対象部品の位置関係を示し続ける。
    // persistentEyeFrom があるときは playEyeAnimation の終わりと同じ見え方
    // （目は from の位置に止まり、世界側が回った状態＋向きの矢印）で置く。
    // 位置決め・枠外クランプ・点線更新はレンダーループの updateGazeEye が共通で行う。
    if (persistentEyePosition) {
      const eye = createEyeObject();
      const line = createGazeLine();
      sceneInstance.add(eye);
      sceneInstance.add(line);
      eyeRef.current = eye;
      eyeLineRef.current = line;
      const motion = followMotion(persistentEyeFrom, persistentEyePosition);
      const shown = motion ? persistentEyeFrom : persistentEyePosition;
      eyeTruePosRef.current = { x: shown.x, y: shown.y, z: shown.z };
      if (motion) {
        frame.quaternion.copy(motion.worldTurn);
        const arrow = createDirectionArrow();
        sceneInstance.add(arrow);
        eyeArrowRef.current = arrow;
        eyeDirRef.current = motion.tangent;
      }
    }

    // Model loading
    placeModel(sceneInstance, loadModel(glbPath, sceneInstance, shearValue, addColor));

    // 目と一緒に動く画面：世界側を回すアニメーションを進める
    const identity = new Quaternion();
    const updateFrame = () => {
      const anim = frameAnimRef.current;
      if (!anim) return;
      const t = Math.min(1, (performance.now() - anim.start) / anim.duration);
      frame.quaternion.slerpQuaternions(identity, anim.to, easeInOutCubic(t));
      if (t >= 1) frameAnimRef.current = null;
    };

    // 目オブジェクト・点線の毎フレーム更新（枠外クランプ→点線/矢印更新）
    const updateGazeEye = (cam) => {
      const eye = eyeRef.current;
      if (!eye) return;

      const truePos = eyeTruePosRef.current;
      if (!truePos) return;

      // 投影前にカメラ行列を最新化（renderよりも前に投影するため）
      cam.updateMatrixWorld();
      cam.matrixWorldInverse.copy(cam.matrixWorld).invert();

      const { offFrame, world } = clampGazeToFrame(cam, truePos);
      eye.position.copy(world);
      eye.lookAt(0, 0, 0);

      // 枠外なら半透明＋外向き矢印を表示して「視点は枠外」と明示
      const fadeMeshes = eye.userData.fadeMeshes || [];
      fadeMeshes.forEach((m) => { if (m.material) m.material.opacity = offFrame ? 0.55 : 1; });
      // 向きの矢印があるときは枠外の矢印を出さない（目に矢印が2本付くと、どちらが動いた向きか分からないため）
      const dirArrow = eyeArrowRef.current;
      if (eye.userData.beyondArrow) eye.userData.beyondArrow.visible = offFrame && !dirArrow;
      if (dirArrow && eyeDirRef.current) {
        dirArrow.position.copy(world).addScaledVector(eyeDirRef.current, EYE_ARROW_OFFSET);
        dirArrow.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), eyeDirRef.current);
        // 目が枠の端に寄っていると矢印の先が枠から切れるので、はみ出したぶんだけ枠の内側へずらす
        const tip = dirArrow.position.clone().addScaledVector(eyeDirRef.current, EYE_ARROW_LENGTH).project(cam);
        const limit = 0.95;
        const inX = Math.max(-limit, Math.min(limit, tip.x));
        const inY = Math.max(-limit, Math.min(limit, tip.y));
        if (inX !== tip.x || inY !== tip.y) {
          const moved = new Vector3(inX, inY, tip.z).unproject(cam);
          dirArrow.position.add(moved.sub(tip.clone().unproject(cam)));
        }
      }

      // 原点→目（表示位置）の点線を更新
      const line = eyeLineRef.current;
      if (line) {
        const posAttr = line.geometry.getAttribute('position');
        posAttr.setXYZ(0, 0, 0, 0);
        posAttr.setXYZ(1, world.x, world.y, world.z);
        posAttr.needsUpdate = true;
        line.computeLineDistances();
      }
    };

    // Animation loop
    const tiltQuaternion = new Quaternion();
    const tiltAxis = new Vector3(0, 0, 1);

    let rafId = null;
    const animate = () => {
      rafId = requestAnimationFrame(animate);
      if (isOrbitControlsEnabled) {
        controls.update();
      }
      if (cameraTiltAngle) {
        tiltQuaternion.setFromAxisAngle(tiltAxis, cameraTiltAngle);
        camera.quaternion.multiply(tiltQuaternion);
      }
      updateFrame(); // 目と一緒に動く画面では世界側を回す
      updateGazeEye(camera); // 目オブジェクトの位置・点線・枠外クランプを毎フレーム更新
      rendererInstance.render(sceneInstance, camera);
    };
    rafId = requestAnimationFrame(animate);

    // Set state
    setRenderer(rendererInstance);
    setScene(sceneInstance);

    // Cleanup
    return () => {
      // stop the animation loop
      try {
        if (rafId) cancelAnimationFrame(rafId);
      } catch (_) {}
      // 命令的アニメーションのrAFも停止
      try {
        if (gazeRafRef.current) cancelAnimationFrame(gazeRafRef.current);
      } catch (_) {}
      gazeRafRef.current = null;
      eyeRef.current = null;
      eyeLineRef.current = null;
      eyeTruePosRef.current = null;
      frameAnimRef.current = null;
      frameRef.current = null;
      eyeArrowRef.current = null;
      eyeDirRef.current = null;
      // remove controls event listener and dispose controls
      try {
        controls.removeEventListener && controls.removeEventListener('change', onControlsChange);
        if (controls.dispose) controls.dispose();
      } catch (_) {}
      // dispose renderer and clear scene
      try { rendererInstance.dispose(); } catch (_) {}
      try { sceneInstance.clear(); } catch (_) {}
    };
    // 着色（addColor）の変更ではシーンを作り直さない（下の effect でモデルだけ差し替える）。
    // 作り直すとカメラが初期位置へ戻り、学習者が回した視点やヒントの視点が失われるため（2026-10-12）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef, glbPath, isModelVisible, isGridVisible, isOrbitControlsEnabled, shearValue, cameraPosition, cameraTiltAngle, setCameraPosition, persistentEyePosition, persistentEyeFrom]);

  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.enabled = isOrbitControlsEnabled; // isOrbitControlsEnabledの変更に対応
    }
  }, [isOrbitControlsEnabled]);

  // 着色だけが変わったときは、カメラ・コントロールはそのままでモデルだけ差し替える
  useEffect(() => {
    const sc = sceneRef.current;
    if (!sc || builtColorRef.current === addColor) return;
    builtColorRef.current = addColor;
    placeModel(sc, loadModel(glbPath, sc, shearValue, addColor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addColor]);

  useEffect(() => {
    if (!cameraRef.current || !cameraPosition) return;

    // イージングでカメラ位置をアニメーションする
    const duration = 1600; // ms
    const start = performance.now();
    const startPos = {
      x: cameraRef.current.position.x,
      y: cameraRef.current.position.y,
      z: cameraRef.current.position.z,
    };
    const endPos = { x: cameraPosition.x, y: cameraPosition.y, z: cameraPosition.z };
    let rafId = null;

    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    const step = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const k = easeInOutCubic(t);
      cameraRef.current.position.set(
        startPos.x + (endPos.x - startPos.x) * k,
        startPos.y + (endPos.y - startPos.y) * k,
        startPos.z + (endPos.z - startPos.z) * k
      );
      // OrbitControlsがある場合は内部状態を更新する
      if (controlsRef.current && typeof controlsRef.current.update === 'function') {
        controlsRef.current.update();
      }
      setCameraPosition({ x: cameraRef.current.position.x, y: cameraRef.current.position.y, z: cameraRef.current.position.z });

      if (t < 1) {
        rafId = requestAnimationFrame(step);
      }
    };

    rafId = requestAnimationFrame(step);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [cameraPosition, setCameraPosition]);

  useEffect(() => {
    if (!cameraRef.current || !controlsRef.current || !cameraTarget) return;
    controlsRef.current.target.set(cameraTarget.x, cameraTarget.y, cameraTarget.z);
    cameraRef.current.lookAt(cameraTarget.x, cameraTarget.y, cameraTarget.z);
    controlsRef.current.update();
  }, [cameraTarget]);

  // --- 命令的アニメーションAPI（シーンを再構築せずにカメラ/目を動かす） ---

  const getTarget = useCallback(() => {
    const t = controlsRef.current && controlsRef.current.target;
    return t ? { x: t.x, y: t.y, z: t.z } : { x: 0, y: 0, z: 0 };
  }, []);

  // OrbitControlsのユーザー操作を一時的に有効/無効化（アニメーション中の競合防止）
  const setOrbitEnabled = useCallback((enabled) => {
    if (controlsRef.current) controlsRef.current.enabled = !!enabled;
  }, []);

  // カメラを即座に指定位置へ（アニメーションなし）
  const snapCameraTo = useCallback((pos) => {
    if (gazeRafRef.current) { cancelAnimationFrame(gazeRafRef.current); gazeRafRef.current = null; }
    const cam = cameraRef.current;
    if (!cam || !pos) return;
    cam.position.set(pos.x, pos.y, pos.z);
    const tgt = getTarget();
    cam.lookAt(tgt.x, tgt.y, tgt.z);
    if (controlsRef.current && controlsRef.current.update) controlsRef.current.update();
  }, [getTarget]);

  // カメラを duration ミリ秒かけてイージング移動（既存レンダーループがそのまま描画する）
  const animateCameraTo = useCallback((targetPos, duration = 2000, onDone) => {
    const cam = cameraRef.current;
    if (!cam || !targetPos) { if (typeof onDone === 'function') onDone(); return; }
    if (gazeRafRef.current) cancelAnimationFrame(gazeRafRef.current);
    const startPos = { x: cam.position.x, y: cam.position.y, z: cam.position.z };
    const tgt = getTarget();
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const k = easeInOutCubic(t);
      cam.position.set(
        startPos.x + (targetPos.x - startPos.x) * k,
        startPos.y + (targetPos.y - startPos.y) * k,
        startPos.z + (targetPos.z - startPos.z) * k
      );
      cam.lookAt(tgt.x, tgt.y, tgt.z);
      if (controlsRef.current && controlsRef.current.update) controlsRef.current.update();
      if (t < 1) {
        gazeRafRef.current = requestAnimationFrame(step);
      } else {
        gazeRafRef.current = null;
        if (typeof onDone === 'function') onDone();
      }
    };
    gazeRafRef.current = requestAnimationFrame(step);
  }, [getTarget]);

  // 目オブジェクトと点線をシーンから除去し、世界側の回転も戻す
  const clearEye = useCallback(() => {
    frameAnimRef.current = null;
    if (frameRef.current) frameRef.current.quaternion.identity();
    eyeTruePosRef.current = null;
    if (sceneRef.current) {
      if (eyeRef.current) {
        sceneRef.current.remove(eyeRef.current);
        disposeObject3D(eyeRef.current);
      }
      if (eyeLineRef.current) {
        sceneRef.current.remove(eyeLineRef.current);
        disposeObject3D(eyeLineRef.current);
      }
      if (eyeArrowRef.current) {
        sceneRef.current.remove(eyeArrowRef.current);
        disposeObject3D(eyeArrowRef.current);
      }
    }
    eyeRef.current = null;
    eyeLineRef.current = null;
    eyeArrowRef.current = null;
    eyeDirRef.current = null;
  }, []);

  // 目が fromPos→toPos へ duration ミリ秒かけて動くようすを、画面を目と一緒に動かして見せる
  // （2026-10-19。目は fromPos の位置に止まって見え、世界側が逆向きに回る。目には動く向きの矢印を添える）。
  // 世界側の回転・枠外クランプ・点線更新はメインレンダーループ(updateFrame/updateGazeEye)が行う。
  const playEyeAnimation = useCallback((fromPos, toPos, duration = 2000) => {
    if (!sceneRef.current || !fromPos || !toPos) return;
    clearEye();
    const eye = createEyeObject();
    const line = createGazeLine();
    sceneRef.current.add(eye);
    sceneRef.current.add(line);
    eyeRef.current = eye;
    eyeLineRef.current = line;
    eyeTruePosRef.current = { x: fromPos.x, y: fromPos.y, z: fromPos.z };
    const motion = followMotion(fromPos, toPos);
    if (motion) {
      const arrow = createDirectionArrow();
      sceneRef.current.add(arrow);
      eyeArrowRef.current = arrow;
      eyeDirRef.current = motion.tangent;
      frameAnimRef.current = { to: motion.worldTurn, duration, start: performance.now() };
    }
  }, [clearEye]);

  return {
    renderer,
    scene,
    camera: cameraRef.current,
    isRendererReady,
    controlsRef,
    animateCameraTo,
    snapCameraTo,
    playEyeAnimation,
    clearEye,
    setOrbitEnabled,
  };
}